import { Button, Form, Input, InputNumber, Modal, Select, message } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse, roundOffToDecimal } from '../../utils/DataAccessUtils';
import PartEquipmentCostFormField from '../common/PartEquipmentCostFormField';
import { partCatalogsRestCrudThunksForSupplier } from '../../thunks/part_catalogs_thunks';
import { connect } from 'react-redux';
import { equipmentCatalogsRestCrudThunksForSupplier } from '../../thunks/equipment_catalogs_thunks';
import { updateLineItemQuantityForSupplier } from '../../thunks/purchase_order_line_items_thunks';
import ChoosePRForPOLineItemModal from './ChoosePRForPOLineItemModal';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { OWButton } from '../ow_button/OWButton';
import { StockLocationDropdownRecord } from '../StockLocationDropdownRecord';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { getRecordsForCompositeTargetCollection } from '../../reducers/standard_reducer_utils';
import { isGLCodeAllowedBySettings } from '../../utils/InventoryUtils';

const FORM_ID = 'edit-cost-form';
const SL_TC_NAME = 'viaStockLocationIndex';

const EditPOLineItemModal: FC<any> = ({
	cost,
	quantity,
	title,
	onCancel,
	onSuccess,
	record,
	fetchPartsCatalog,
	fetchEquipmentsCatalog,
	partEquipmentVendorId,
	poCurrencyId,
	updateQuantity,
	viaStockLocationId,
	stockLocations,
	fetchMultipleStockLocations,
	fetchStockLocations,
	allowViaStockLocation = false,
	companyConfig,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [updating, setUpdating] = useState(false);
	const [choosePRModalVisible, setChoosePRModalVisible] = useState(false);
	const [diffQuantity, setDiffQuantity] = useState(0);

	const { getFieldsValue } = form;

	const unitCost = Form.useWatch('unitCost', form);
	const totalQuantity = Form.useWatch('totalQuantity', form);

	const { setFieldsValue } = form;

	const _hidePRModal = () => setChoosePRModalVisible(false);

	useEffect(() => {
		const changedValue = parseInt(totalQuantity);
		!isNaN(changedValue) && setFieldsValue({ totalQuantity: changedValue });
	}, [totalQuantity, setFieldsValue]);

	const prLineItems = useMemo(
		() => nullSafeGetOrElse('supplierPurchaseRequestLineItems', record, []),
		[record]
	);

	const getViaStockLocationRecord = useCallback(
		(slId) => {
			const slRecords = getRecordsForCompositeTargetCollection(stockLocations, SL_TC_NAME);
			return slId ? slRecords.find((_) => _.id === slId) : undefined;
		},
		[stockLocations]
	);

	const updateQuantityAndSubmit = useCallback(
		(affectedPurchaseRequestLineItemId) => {
			_hidePRModal();
			setUpdating(true);

			const values = getFieldsValue();

			updateQuantity(record.id, {
				...values,
				totalCost: values.cost,
				affectedPurchaseRequestLineItemId,
			})
				.then(() =>
					onSuccess(
						totalQuantity,
						values.cost,
						getViaStockLocationRecord(values.viaStockLocationId),
						values.glCode
					)
				)
				.catch((err) => message.error(err))
				.finally(() => setUpdating(false));
		},
		[getFieldsValue, getViaStockLocationRecord, onSuccess, record.id, totalQuantity, updateQuantity]
	);

	const onSubmit = useCallback(
		(values) => {
			if (values.totalQuantity !== quantity) {
				setDiffQuantity(values.totalQuantity - quantity);
				if (prLineItems.length > 1) {
					setChoosePRModalVisible(true);
				} else {
					const prLineItemId = nullSafeGet('0.id', prLineItems);
					updateQuantityAndSubmit(prLineItemId);
				}
			} else {
				onSuccess(
					quantity,
					values.cost,
					getViaStockLocationRecord(values.viaStockLocationId),
					values.glCode
				);
			}
		},
		[getViaStockLocationRecord, onSuccess, prLineItems, quantity, updateQuantityAndSubmit]
	);

	const initialValues = useMemo(
		() => ({
			glCode: record.glCode,
			unitCost: cost !== undefined ? roundOffToDecimal((cost || 0) / (quantity || 1)) : undefined,
			cost,
			totalQuantity: quantity,
		}),
		[cost, quantity, record.glCode]
	);

	const entityId = record.partId || record.equipmentTypeId;

	const entityName = record.partId ? 'partId' : 'equipmentTypeId';

	const vendorId =
		partEquipmentVendorId ||
		nullSafeGet('equipmentCatalog.partEquipmentVendorId', record) ||
		nullSafeGet('partCatalog.partEquipmentVendorId', record);

	const fetchMethod = record.partId ? fetchPartsCatalog : fetchEquipmentsCatalog;

	const entityCost = record.partId
		? nullSafeGet('part.cost', record)
		: nullSafeGet('equipmentType.cost', record);

	const glCodeEnabled = useMemo(() => isGLCodeAllowedBySettings(companyConfig), [companyConfig]);

	return (
		<Modal
			visible={true}
			width={600}
			title={title}
			closable
			onCancel={onCancel}
			forceRender
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<OWButton
					type="primary"
					size="large"
					key="submit"
					htmlType="submit"
					form={FORM_ID}
					loading={updating}
				>
					{`Update ${record.isEquipmentLine ? 'Equipment' : 'Part'}`}
				</OWButton>,
			]}
		>
			<Form
				id={FORM_ID}
				form={form}
				layout="vertical"
				requiredMark={false}
				initialValues={initialValues}
				onFinish={onSubmit}
			>
				{glCodeEnabled ? (
					<Form.Item name="glCode" label="GL Code">
						<Input style={{ width: '100%' }} />
					</Form.Item>
				) : null}
				<Form.Item
					name="totalQuantity"
					label="Total Quantity"
					rules={[
						{
							required: true,
							message: 'Please add valid quantity.',
						},
						{
							type: 'number',
							min: 1,
							message: 'Quantity cannot be less than 1',
						},
					]}
				>
					<InputNumber style={{ width: '100%' }} step={1} disabled={!record.id} />
				</Form.Item>
				<PartEquipmentCostFormField
					entityId={entityId}
					unitCost={unitCost}
					entityName={entityName}
					vendorId={vendorId}
					fetchMethod={fetchMethod}
					setFieldsValue={setFieldsValue}
					form={form}
					currencyId={poCurrencyId}
					quantity={quantity}
					entityCost={entityCost}
				/>
				{record.equipmentTypeId && allowViaStockLocation ? (
					<Form.Item
						name="viaStockLocationId"
						label="Via Stock Location"
						initialValue={viaStockLocationId}
					>
						<OWAsyncSelect
							stateSlice={stockLocations}
							allowClear
							targetCollectionName={SL_TC_NAME}
							fetchMultiple={(ids, targetCollectionName) => {
								fetchMultipleStockLocations(ids, targetCollectionName);
							}}
							fetchData={(
								searchText,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) => {
								fetchStockLocations(
									{ name: searchText },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								);
							}}
							renderRecord={(record) => (
								<Select.Option key={`${record.id} - ${record.name}`} value={record.id}>
									<StockLocationDropdownRecord record={record} />
								</Select.Option>
							)}
						/>
					</Form.Item>
				) : null}
			</Form>
			{choosePRModalVisible ? (
				<ChoosePRForPOLineItemModal
					onCancel={_hidePRModal}
					onSuccess={updateQuantityAndSubmit}
					record={record}
					diffQuantity={diffQuantity}
				/>
			) : null}
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	stockLocations: state.stock_locations,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchPartsCatalog: (params, targetCollectionName) =>
		dispatch(partCatalogsRestCrudThunksForSupplier.read(params, targetCollectionName)),
	fetchEquipmentsCatalog: (params, targetCollectionName) =>
		dispatch(equipmentCatalogsRestCrudThunksForSupplier.read(params, targetCollectionName)),
	updateQuantity: (id, params) => dispatch(updateLineItemQuantityForSupplier(id, params)),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.readLite(
				{ ...params, allowEquipmentForwarding: true, noIsActiveFilter: false },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditPOLineItemModal);
